// The person a LINK names (`login_hint`) and the address without it — the
// address-bar half of "every link lands signed in" (ssoMemory.ts). Pure.
//
// A hint arrives in the query (`/<page>?login_hint=<upn>`, the Launchpad's
// cross-app handshake and every email the shared Helpdesk sender delivers) or
// inside the hash (`…#/x?login_hint=<upn>`, where a link that already carried
// a hash gets it appended to the whole URL).

/** A query parameter inside the hash (`#/route?key=value`), or null. */
export function hashQueryParam(hash: string, name: string): string | null {
  const h = hash.replace(/^#/, '');
  const q = h.indexOf('?');
  return q === -1 ? null : new URLSearchParams(h.slice(q + 1)).get(name);
}

/** The person a link names, in the query or inside the hash. Blank is none. */
export function linkLoginHint(search: string, hash: string): string | null {
  const fromQuery = new URLSearchParams(search).get('login_hint')?.trim();
  if (fromQuery) return fromQuery;
  return hashQueryParam(hash, 'login_hint')?.trim() || null;
}

/**
 * The page carrying Entra's answer (`code` / `error` in the query or the
 * hash): never start another sign-in from it — and never from the hidden
 * renewal iframe, which loads this same page with that answer.
 */
export function isAuthResponse(search: string, hash: string): boolean {
  return /[?#&](code|error)=/.test(hash + search);
}

/**
 * The address bar without `login_hint` — in the query or inside the hash — or
 * null when there is none to strip. Once signed in the hint has done its work,
 * and an address copied into Teams would otherwise carry the SHARER's hint:
 * the colleague who opens it would be signed in as — or asked for the
 * password of — somebody else (PunchWISE §30, 2026-10-26). The path, the hash
 * route and every other query key are kept.
 */
export function addressWithoutLoginHint(pathname: string, search: string, hash: string): string | null {
  const params = new URLSearchParams(search);
  const inQuery = params.has('login_hint');
  params.delete('login_hint');
  const query = params.toString();
  const h = hash.replace(/^#/, '');
  const q = h.indexOf('?');
  let route = hash;
  let inHash = false;
  if (q !== -1) {
    const hashParams = new URLSearchParams(h.slice(q + 1));
    if (hashParams.has('login_hint')) {
      inHash = true;
      hashParams.delete('login_hint');
      const rest = hashParams.toString();
      route = `#${h.slice(0, q)}${rest ? `?${rest}` : ''}`;
    }
  }
  if (!inQuery && !inHash) return null;
  return `${pathname || '/'}${query ? `?${query}` : ''}${route}`;
}
